import { action } from '@ember/object';
import Component from '@glimmer/component';

import $ from 'jquery';

import type { SingleCheckboxValue } from 'webapp/components/form/checkbox';
import type { GroupSelected } from 'webapp/components/form/checkbox-group';

interface CheckboxArgsBase {
	id: string;
	name?: string;
	value?: SingleCheckboxValue;
	isDisabled?: boolean;
	onBeforeChange?: (changingTo: boolean) => Promise<unknown>;
}

interface CheckboxArgsSingle extends CheckboxArgsBase {
	selected: boolean | null | undefined;
	onChange: (newSelected: boolean) => void;
}

interface CheckboxArgsGroup extends CheckboxArgsBase {
	selected: GroupSelected;
	onChange: (newSelected: GroupSelected) => void;
}

export type Args = CheckboxArgsSingle | CheckboxArgsGroup;

interface FormCheckboxSingleComponentInterface<T> {
	Element: HTMLInputElement; // this is needed for ...attributes
	Args: T;
}

export default class FormCheckboxSingleComponent extends Component<
	FormCheckboxSingleComponentInterface<Args>
> {
	get name() {
		let name = this.args.name;
		if (this.isPartOfCheckboxGroup(this.args)) {
			name += '[]';
		}
		return name;
	}

	get isChecked() {
		let isChecked = false;

		if (this.isPartOfCheckboxGroup(this.args)) {
			const args = this.args as CheckboxArgsGroup;
			isChecked = args.selected.includes(args.value!);
		} else {
			const args = this.args as CheckboxArgsSingle;
			isChecked = !!args.selected;
		}

		return isChecked;
	}

	onBeforeChange(changingTo: boolean) {
		if (this.args.onBeforeChange) {
			// supporting only for single checkboxes, not groups
			return this.args.onBeforeChange(changingTo);
		} else {
			return Promise.resolve();
		}
	}

	changeSelected(isChecked: boolean) {
		if (this.isPartOfCheckboxGroup(this.args)) {
			const args = this.args as CheckboxArgsGroup;
			let selected = Array.from(args.selected);

			const value = args.value!;

			if (isChecked && !selected.includes(value)) {
				selected = [...selected, value];
			} else if (!isChecked && selected.includes(value)) {
				selected = selected.filter((v) => v !== value);
			}

			args.onChange(selected);
		} else {
			const args = this.args as CheckboxArgsSingle;
			args.onChange(isChecked);
		}
	}

	private isPartOfCheckboxGroup(args: Args): args is CheckboxArgsGroup {
		return 'selected' in args && Array.isArray(args.selected);
	}

	@action
	onChange(e: InputEvent) {
		const el = $(e.target as HTMLElement),
			isChecked = el.is(':checked');
		this.onBeforeChange(isChecked).then(
			() => {
				this.changeSelected(isChecked);
			},
			() => {
				el.prop('checked', !isChecked);
			},
		);
	}

	<template>
		<input
			...attributes
			type="checkbox"
			id={{@id}}
			name={{this.name}}
			value={{@value}}
			disabled={{@isDisabled}}
			checked={{this.isChecked}}
			{{! @glint-expect-error: it's probably fine :P }}
			onchange={{this.onChange}}
		/>
	</template>
}
